import { useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { Link } from '@tanstack/react-router';
import { T, useGT } from 'gt-react';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Input } from '@/components/ui/input';
import type { WorkbookEditor } from './useWorkbookEditor';

const subscribeToNothing = () => () => undefined;
const pageHeaderSlot = () => document.getElementById('app-page-header');
const noPageHeaderSlot = () => null;

export function WorkbookHeader({
  editor,
  canUpdate,
  busy,
}: {
  editor: WorkbookEditor;
  canUpdate: boolean;
  busy: boolean;
}) {
  const gt = useGT();
  // The editor owns the live name; the layout owns the header's DOM slot,
  // read again once mounted (it is not in the document on a first render).
  const header = useSyncExternalStore(
    subscribeToNothing,
    pageHeaderSlot,
    noPageHeaderSlot,
  );
  if (!header) return null;
  return createPortal(
    <Breadcrumb>
      <BreadcrumbList className="flex-nowrap">
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link to="/workbooks">
              <T context="workbook editor parent breadcrumb">Workbook</T>
            </Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem className="min-w-0">
          <Input
            className="w-64 max-w-[30vw] border-transparent px-1 font-medium text-foreground shadow-none"
            aria-label={gt('Workbook name', { context: 'workbook name field' })}
            value={editor.definition.name}
            title={editor.definition.name}
            maxLength={200}
            disabled={!canUpdate || busy}
            onChange={(event) =>
              editor.edits.workbook.update((definition) => ({
                ...definition,
                name: event.target.value,
              }))
            }
          />
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>,
    header,
  );
}
